import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { 
  ArrowLeft, Save, Eye, EyeOff, Settings, FileText, Image, 
  BarChart3, Download, Users, TrendingUp, Facebook, Smartphone,
  Globe, MessageSquare, DollarSign, HelpCircle, Star, Megaphone,
  RefreshCw, ExternalLink, Palette, Layout
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingAdmin = () => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [analytics, setAnalytics] = useState({});

  useEffect(() => {
    if (!authLoading && (!currentUser || currentUser.role !== 'admin')) {
      navigate('/dashboard');
      return;
    }
    loadConfig();
    loadAnalytics();
  }, [currentUser, authLoading, navigate]);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('notimax_token');
      const response = await axios.get(`${API_URL}/api/landing/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
      toast({ title: 'Error', description: 'No se pudo cargar la configuración', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const token = localStorage.getItem('notimax_token');
      const response = await axios.get(`${API_URL}/api/landing/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('notimax_token');
      await axios.put(`${API_URL}/api/landing/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Guardado', description: 'Configuración actualizada correctamente' });
    } catch (error) {
      console.error('Error saving config:', error);
      toast({ title: 'Error', description: 'No se pudo guardar la configuración', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (sectionName, field, value) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionName]: {
          ...prev.sections[sectionName],
          [field]: value
        }
      }
    }));
  };

  const toggleSection = (sectionName) => {
    updateSection(sectionName, 'enabled', !config.sections[sectionName]?.enabled);
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!config) return null;

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'sections', label: 'Secciones', icon: Layout },
    { id: 'hero', label: 'Hero', icon: Megaphone },
    { id: 'features', label: 'Características', icon: Star },
    { id: 'pricing', label: 'Precios', icon: DollarSign },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
    { id: 'testimonials', label: 'Testimonios', icon: MessageSquare },
    { id: 'seo', label: 'SEO', icon: Globe },
    { id: 'analytics', label: 'Analíticas', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Volver
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Editor de Landing Page</h1>
                <p className="text-sm text-gray-500">Configura tu página de inicio</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => window.open('/', '_blank')}>
                <Eye className="w-4 h-4 mr-2" /> Vista Previa
              </Button>
              <Button onClick={saveConfig} disabled={saving} className="bg-gradient-to-r from-blue-500 to-green-500">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar Cambios
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <Card>
              <CardContent className="p-2">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition ${
                        activeTab === tab.id 
                          ? 'bg-blue-50 text-blue-600' 
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-6">
            {/* General Settings */}
            {activeTab === 'general' && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Facebook className="w-5 h-5 mr-2 text-blue-600" />
                      Facebook Pixel
                    </CardTitle>
                    <CardDescription>Configura tu Pixel de Facebook para rastrear conversiones</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="pixelId">Pixel ID</Label>
                      <Input
                        id="pixelId"
                        placeholder="Ej: 123456789012345"
                        value={config.facebookPixelId || ''}
                        onChange={(e) => setConfig({ ...config, facebookPixelId: e.target.value })}
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Obtén tu Pixel ID en <a href="https://business.facebook.com/events_manager" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Facebook Events Manager <ExternalLink className="w-3 h-3 inline" /></a>
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Smartphone className="w-5 h-5 mr-2 text-green-600" />
                      Descarga de Aplicaciones
                    </CardTitle>
                    <CardDescription>Configura los enlaces de descarga de tu aplicación</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="apkUrl">📱 URL APK Android</Label>
                      <Input
                        id="apkUrl"
                        placeholder="https://ejemplo.com/notimax.apk"
                        value={config.apkUrl || ''}
                        onChange={(e) => setConfig({ ...config, apkUrl: e.target.value })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Enlace para descargar la aplicación Android (APK)</p>
                    </div>
                    <div>
                      <Label htmlFor="apkUrlIos">🍎 URL iOS (App Store)</Label>
                      <Input
                        id="apkUrlIos"
                        placeholder="https://apps.apple.com/app/notimax/id123456"
                        value={config.apkUrlIos || ''}
                        onChange={(e) => setConfig({ ...config, apkUrlIos: e.target.value })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Enlace de la App Store para iOS</p>
                    </div>
                    <div>
                      <Label htmlFor="apkUrlPwa">🌐 URL PWA</Label>
                      <Input
                        id="apkUrlPwa"
                        placeholder="https://notimax.app"
                        value={config.apkUrlPwa || 'https://notimax.app'}
                        onChange={(e) => setConfig({ ...config, apkUrlPwa: e.target.value })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Enlace de la Progressive Web App (ya preconfigurado)</p>
                    </div>
                    <div>
                      <Label htmlFor="apkVersion">Versión</Label>
                      <Input
                        id="apkVersion"
                        placeholder="1.0.0"
                        value={config.apkVersion || ''}
                        onChange={(e) => setConfig({ ...config, apkVersion: e.target.value })}
                      />
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Sections Toggle */}
            {activeTab === 'sections' && (
              <Card>
                <CardHeader>
                  <CardTitle>Visibilidad de Secciones</CardTitle>
                  <CardDescription>Activa o desactiva las secciones de la landing page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.keys(config.sections).map(sectionKey => (
                    <div key={sectionKey} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium capitalize">{sectionKey === 'howItWorks' ? 'Cómo Funciona' : sectionKey === 'cta' ? 'Llamado a la Acción' : sectionKey}</p>
                        <p className="text-sm text-gray-500">
                          {config.sections[sectionKey]?.enabled ? 'Visible en la landing' : 'Oculto'}
                        </p>
                      </div>
                      <Switch
                        checked={config.sections[sectionKey]?.enabled || false}
                        onCheckedChange={() => toggleSection(sectionKey)}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Hero Section */}
            {activeTab === 'hero' && (
              <Card>
                <CardHeader>
                  <CardTitle>Sección Hero</CardTitle>
                  <CardDescription>El encabezado principal de tu landing page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mb-4">
                    <span className="font-medium">Sección activa</span>
                    <Switch
                      checked={config.sections.hero?.enabled || false}
                      onCheckedChange={() => toggleSection('hero')}
                    />
                  </div>
                  <div>
                    <Label>Título Principal</Label>
                    <Input
                      value={config.sections.hero?.title || ''}
                      onChange={(e) => updateSection('hero', 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Subtítulo</Label>
                    <Input
                      value={config.sections.hero?.subtitle || ''}
                      onChange={(e) => updateSection('hero', 'subtitle', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Textarea
                      value={config.sections.hero?.description || ''}
                      onChange={(e) => updateSection('hero', 'description', e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Botón Principal</Label>
                      <Input
                        value={config.sections.hero?.ctaPrimary || ''}
                        onChange={(e) => updateSection('hero', 'ctaPrimary', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Botón Secundario</Label>
                      <Input
                        value={config.sections.hero?.ctaSecondary || ''}
                        onChange={(e) => updateSection('hero', 'ctaSecondary', e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Features Section */}
            {activeTab === 'features' && (
              <Card>
                <CardHeader>
                  <CardTitle>Características</CardTitle>
                  <CardDescription>Los beneficios y características de tu producto</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mb-4">
                    <span className="font-medium">Sección activa</span>
                    <Switch
                      checked={config.sections.features?.enabled || false}
                      onCheckedChange={() => toggleSection('features')}
                    />
                  </div>
                  <div>
                    <Label>Título</Label>
                    <Input
                      value={config.sections.features?.title || ''}
                      onChange={(e) => updateSection('features', 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Subtítulo</Label>
                    <Input
                      value={config.sections.features?.subtitle || ''}
                      onChange={(e) => updateSection('features', 'subtitle', e.target.value)}
                    />
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <Label className="mb-3 block">Características ({config.sections.features?.items?.length || 0})</Label>
                    {config.sections.features?.items?.map((item, index) => (
                      <div key={index} className="p-4 border rounded-lg mb-3 bg-gray-50">
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <Label className="text-xs">Icono</Label>
                            <Input
                              value={item.icon || ''}
                              onChange={(e) => {
                                const newItems = [...config.sections.features.items];
                                newItems[index].icon = e.target.value;
                                updateSection('features', 'items', newItems);
                              }}
                              placeholder="MessageSquare"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Título</Label>
                            <Input
                              value={item.title || ''}
                              onChange={(e) => {
                                const newItems = [...config.sections.features.items];
                                newItems[index].title = e.target.value;
                                updateSection('features', 'items', newItems);
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Descripción</Label>
                          <Textarea
                            value={item.description || ''}
                            onChange={(e) => {
                              const newItems = [...config.sections.features.items];
                              newItems[index].description = e.target.value;
                              updateSection('features', 'items', newItems);
                            }}
                            rows={2}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pricing Section */}
            {activeTab === 'pricing' && (
              <Card>
                <CardHeader>
                  <CardTitle>Precios</CardTitle>
                  <CardDescription>Configura los planes y precios</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mb-4">
                    <span className="font-medium">Sección activa</span>
                    <Switch
                      checked={config.sections.pricing?.enabled || false}
                      onCheckedChange={() => toggleSection('pricing')}
                    />
                  </div>
                  <div>
                    <Label>Título</Label>
                    <Input
                      value={config.sections.pricing?.title || ''}
                      onChange={(e) => updateSection('pricing', 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Subtítulo</Label>
                    <Input
                      value={config.sections.pricing?.subtitle || ''}
                      onChange={(e) => updateSection('pricing', 'subtitle', e.target.value)}
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6 pt-4">
                    {/* Monthly Plan */}
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-3">Plan Mensual</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Precio</Label>
                            <Input
                              value={config.sections.pricing?.monthly?.price || ''}
                              onChange={(e) => {
                                const monthly = { ...config.sections.pricing.monthly, price: e.target.value };
                                updateSection('pricing', 'monthly', monthly);
                              }}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Moneda</Label>
                            <Input
                              value={config.sections.pricing?.monthly?.currency || ''}
                              onChange={(e) => {
                                const monthly = { ...config.sections.pricing.monthly, currency: e.target.value };
                                updateSection('pricing', 'monthly', monthly);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Annual Plan */}
                    <div className="p-4 border rounded-lg border-green-300 bg-green-50">
                      <h4 className="font-semibold mb-3">Plan Anual</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Precio</Label>
                            <Input
                              value={config.sections.pricing?.annual?.price || ''}
                              onChange={(e) => {
                                const annual = { ...config.sections.pricing.annual, price: e.target.value };
                                updateSection('pricing', 'annual', annual);
                              }}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Ahorro</Label>
                            <Input
                              value={config.sections.pricing?.annual?.savings || ''}
                              onChange={(e) => {
                                const annual = { ...config.sections.pricing.annual, savings: e.target.value };
                                updateSection('pricing', 'annual', annual);
                              }}
                              placeholder="Ahorra $67"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* FAQ Section */}
            {activeTab === 'faq' && (
              <Card>
                <CardHeader>
                  <CardTitle>Preguntas Frecuentes</CardTitle>
                  <CardDescription>Responde las dudas más comunes</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mb-4">
                    <span className="font-medium">Sección activa</span>
                    <Switch
                      checked={config.sections.faq?.enabled || false}
                      onCheckedChange={() => toggleSection('faq')}
                    />
                  </div>
                  <div>
                    <Label>Título</Label>
                    <Input
                      value={config.sections.faq?.title || ''}
                      onChange={(e) => updateSection('faq', 'title', e.target.value)}
                    />
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <Label className="mb-3 block">Preguntas ({config.sections.faq?.items?.length || 0})</Label>
                    {config.sections.faq?.items?.map((item, index) => (
                      <div key={index} className="p-4 border rounded-lg mb-3 bg-gray-50">
                        <div className="mb-3">
                          <Label className="text-xs">Pregunta</Label>
                          <Input
                            value={item.question || ''}
                            onChange={(e) => {
                              const newItems = [...config.sections.faq.items];
                              newItems[index].question = e.target.value;
                              updateSection('faq', 'items', newItems);
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Respuesta</Label>
                          <Textarea
                            value={item.answer || ''}
                            onChange={(e) => {
                              const newItems = [...config.sections.faq.items];
                              newItems[index].answer = e.target.value;
                              updateSection('faq', 'items', newItems);
                            }}
                            rows={2}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Testimonials Section */}
            {activeTab === 'testimonials' && (
              <Card>
                <CardHeader>
                  <CardTitle>Testimonios</CardTitle>
                  <CardDescription>Opiniones de tus clientes</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mb-4">
                    <span className="font-medium">Sección activa</span>
                    <Switch
                      checked={config.sections.testimonials?.enabled || false}
                      onCheckedChange={() => toggleSection('testimonials')}
                    />
                  </div>
                  <div>
                    <Label>Título</Label>
                    <Input
                      value={config.sections.testimonials?.title || ''}
                      onChange={(e) => updateSection('testimonials', 'title', e.target.value)}
                    />
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <Label className="mb-3 block">Testimonios ({config.sections.testimonials?.items?.length || 0})</Label>
                    {config.sections.testimonials?.items?.map((item, index) => (
                      <div key={index} className="p-4 border rounded-lg mb-3 bg-gray-50">
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <Label className="text-xs">Nombre</Label>
                            <Input
                              value={item.name || ''}
                              onChange={(e) => {
                                const newItems = [...config.sections.testimonials.items];
                                newItems[index].name = e.target.value;
                                updateSection('testimonials', 'items', newItems);
                              }}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Rol/Empresa</Label>
                            <Input
                              value={item.role || ''}
                              onChange={(e) => {
                                const newItems = [...config.sections.testimonials.items];
                                newItems[index].role = e.target.value;
                                updateSection('testimonials', 'items', newItems);
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Testimonio</Label>
                          <Textarea
                            value={item.text || ''}
                            onChange={(e) => {
                              const newItems = [...config.sections.testimonials.items];
                              newItems[index].text = e.target.value;
                              updateSection('testimonials', 'items', newItems);
                            }}
                            rows={2}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* SEO Section */}
            {activeTab === 'seo' && (
              <Card>
                <CardHeader>
                  <CardTitle>SEO y Metadatos</CardTitle>
                  <CardDescription>Optimiza tu página para buscadores</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Título de la Página</Label>
                    <Input
                      value={config.seo?.title || ''}
                      onChange={(e) => setConfig({ ...config, seo: { ...config.seo, title: e.target.value } })}
                    />
                  </div>
                  <div>
                    <Label>Descripción Meta</Label>
                    <Textarea
                      value={config.seo?.description || ''}
                      onChange={(e) => setConfig({ ...config, seo: { ...config.seo, description: e.target.value } })}
                      rows={3}
                    />
                    <p className="text-sm text-gray-500 mt-1">{(config.seo?.description || '').length}/160 caracteres</p>
                  </div>
                  <div>
                    <Label>Palabras Clave</Label>
                    <Input
                      value={config.seo?.keywords || ''}
                      onChange={(e) => setConfig({ ...config, seo: { ...config.seo, keywords: e.target.value } })}
                      placeholder="citas, recordatorios, whatsapp"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Analytics Section */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Visitas Totales</p>
                          <p className="text-3xl font-bold text-gray-900">{analytics.totalVisits || 0}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Descargas APK</p>
                          <p className="text-3xl font-bold text-gray-900">{analytics.apkDownloads || 0}</p>
                        </div>
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                          <Download className="w-6 h-6 text-green-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Registros</p>
                          <p className="text-3xl font-bold text-gray-900">{analytics.signups || 0}</p>
                        </div>
                        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-purple-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Conversiones</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                        <span>Tasa de Descarga</span>
                        <Badge variant="outline">
                          {analytics.totalVisits > 0 
                            ? ((analytics.apkDownloads / analytics.totalVisits) * 100).toFixed(1) 
                            : 0}%
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                        <span>Tasa de Registro</span>
                        <Badge variant="outline">
                          {analytics.totalVisits > 0 
                            ? ((analytics.signups / analytics.totalVisits) * 100).toFixed(1) 
                            : 0}%
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingAdmin;
